/**
 * N1 · Details step 3 of 4 — DATE OF BIRTH only.
 *
 * B6 · the three boxes AUTO-ADVANCE: month → day → year as each fills, and
 * backspace on an empty box steps back. Same ergonomics as the verification code
 * boxes, so a customer never has to tap between fields.
 *
 * PURPOSE: identity verification for the issuer (KYC/OFAC). No age is derived
 * here — age screening is the separate affirmation on the verification step.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { readDetails, readSelectedVariant, saveDetails } from "@/lib/consumer-session";
import { Field, inputClass } from "@/components/consumer/chrome";
import DetailsStepPage from "@/components/consumer/DetailsStepPage";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S6_SECTION, S6_DEFAULTS } from "@/content/screens/s6";
import { cn } from "@/lib/utils";
import {
  dobMissing,
  firstIncompleteStep,
  nextDetailsStep,
  parseDob,
  prevDetailsStep,
} from "@/lib/details-steps";
import { useProgrammeFigures, resolveFigures } from "@/lib/programme-figures";
import { useFieldCompletion } from "@/hooks/useFieldCompletion";
import { useKnownDetailsPrefill } from "@/lib/details-prefill";
import { useConsumerFlow } from "../../ConsumerFlow";

export default function DobStep() {
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const { t } = useSiteContent(S6_SECTION, S6_DEFAULTS);
  const existing = readDetails(program.slug);
  // {minAge} resolves from the configured gate; unresolved = the hint is not shown.
  const figures = useProgrammeFigures(program.slug);

  const [month, setMonth] = useState(() => (existing?.date_of_birth ?? "").slice(5, 7));
  const [day, setDay] = useState(() => (existing?.date_of_birth ?? "").slice(8, 10));
  const [year, setYear] = useState(() => (existing?.date_of_birth ?? "").slice(0, 4));

  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!readSelectedVariant(program.slug)) {
      navigate("../reveal", { replace: true });
      return;
    }
    const step = firstIncompleteStep(readDetails(program.slug), 0);
    if (step === "name" || step === "phone") navigate(`../${step}`, { replace: true });
  }, [program.slug, navigate]);

  /* PREFILL the stored date of birth (identity only, never age-derived). */
  useKnownDetailsPrefill(program.slug, (known) => {
    const iso = known.date_of_birth ?? "";
    if (iso.length < 10) return;
    setMonth((v) => v || iso.slice(5, 7));
    setDay((v) => v || iso.slice(8, 10));
    setYear((v) => v || iso.slice(0, 4));
  });

  const dob = parseDob(month, day, year);
  const missing = dobMissing(dob.state);
  useFieldCompletion("field_dob_complete", !missing);

  function onContinue() {
    saveDetails(program.slug, {
      ...(readDetails(program.slug) ?? {}),
      date_of_birth: dob.iso,
    } as any);
    navigate(nextDetailsStep("dob"));
  }

  const box = cn(inputClass, "text-center px-2");

  return (
    <DetailsStepPage
      testId="s6-dob-step"
      backTo={prevDetailsStep("dob")}
      backLabel={t("s6.header.backLabel")}
      step="3 of 4"
      title={t("s6.contact.dobLabel")}
      missing={missing}
      continueLabel={t("s6.footer.continue")}
      onContinue={onContinue}
    >
      <Field label={t("s6.contact.dobLabel")} hint={resolveFigures(t("s6.contact.dobHint"), figures) ?? undefined}>
        <div className="flex gap-2">
          <input
            ref={monthRef}
            value={month}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 2);
              setMonth(v);
              // AUTO-ADVANCE: two digits, or a leading digit that can only be a
              // single-digit month (4–9), means the month is finished.
              if (v.length === 2 || (v.length === 1 && Number(v) > 1)) dayRef.current?.focus();
            }}
            inputMode="numeric"
            maxLength={2}
            placeholder={t("s6.contact.dobMonthPlaceholder")}
            autoComplete="bday-month"
            aria-label="Month"
            className={cn(box, "flex-1")}
            data-testid="s6-dob-month"
          />
          <input
            ref={dayRef}
            value={day}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 2);
              setDay(v);
              if (v.length === 2 || (v.length === 1 && Number(v) > 3)) yearRef.current?.focus();
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && day === "") monthRef.current?.focus();
            }}
            inputMode="numeric"
            maxLength={2}
            placeholder={t("s6.contact.dobDayPlaceholder")}
            autoComplete="bday-day"
            aria-label="Day"
            className={cn(box, "flex-1")}
            data-testid="s6-dob-day"
          />
          <input
            ref={yearRef}
            value={year}
            onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && year === "") dayRef.current?.focus();
            }}
            inputMode="numeric"
            maxLength={4}
            placeholder={t("s6.contact.dobYearPlaceholder")}
            autoComplete="bday-year"
            aria-label="Year"
            className={cn(box, "flex-[1.4]")}
            data-testid="s6-dob-year"
          />
        </div>
      </Field>
    </DetailsStepPage>
  );
}
